import React, { useState, useEffect, useRef } from "react";

interface Props {
  label: string;
  value: string;
  onSearch?: (search: string) => void;
  options: string[];
  onSelect: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
}

const SearchableAddressDropdown: React.FC<Props> = ({
  label,
  value,
  onSearch,
  options,
  onSelect,
  required,
  disabled,
}) => {
  const [query, setQuery] = useState(value);
  const [lastValidValue, setLastValidValue] = useState(value);
  const [show, setShow] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filteredOptions =
    query.length >= 2 && onSearch
      ? options.filter((opt) => opt.toLowerCase().includes(query.toLowerCase()))
      : options;

  // Sync value from parent when not actively typing
  useEffect(() => {
    if (!isTyping && value !== lastValidValue) {
      setQuery(value);
      setLastValidValue(value);
    }
  }, [value, isTyping]);

  // Trigger search if applicable
  useEffect(() => {
    if (query && query !== lastValidValue && onSearch && query.length >= 2) {
      onSearch(query);
      setShow(true);
    }
  }, [query]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShow(false);
        setIsTyping(false);
        
        const trimmedQuery = query.trim().toLowerCase();
        const match = options.find((opt) => opt.toLowerCase() === trimmedQuery);
        
        if (!match && lastValidValue) {
          setQuery(lastValidValue); 
        } else if (match) {
          setLastValidValue(match);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [query, options, lastValidValue]);

  return (
    <div className="form-floating searchable-dropdown-wrapper" ref={wrapperRef}>
      <input
        type="text"
        className="form-control"
        id={`floating-${label}`}
        placeholder={label}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsTyping(true);
        }}
        onFocus={() => {
          if ((query.length >= 2 && onSearch) || options.length > 0) {
            if (onSearch && query.length >= 2) {
              onSearch(query);
            }
            setShow(true);
          }
        }}
        onBlur={() => {
          setIsTyping(false);
        }}
        required={required}
        disabled={disabled}
      />
      <label htmlFor={`floating-${label}`}>
        {label}
        {required && <span style={{ color: "red" }}>*</span>}
      </label>

      {show && (
        <div className="dropdown-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, idx) => (
              <div
                key={idx}
                className="form-check option-row"
                onMouseDown={() => {
                  onSelect(opt);
                  setQuery(opt);
                  setLastValidValue(opt);
                  setShow(false);
                  setIsTyping(false);
                }}
              >
                <input
                  className="form-check-input"
                  type="radio"
                  name={label}
                  id={`${label}-${idx}`}
                  checked={value === opt}
                  readOnly
                />
                <label className="form-check-label" htmlFor={`${label}-${idx}`}>
                  {opt}
                </label>
              </div>
            ))
          ) : (
            <div className="no-result">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableAddressDropdown;